/**
 * Conversation-kind classifier for ritual_group_conversations (#327).
 *
 * Frontend mirror of the backend classifier
 * (apps/backend/src/services/ritual-group-conversation-kind.ts). The table backs
 * three flavors discriminated by FK nullability:
 *
 *   | flavor          | bookingId | ritualId | coachingSessionId |
 *   |-----------------|-----------|----------|-------------------|
 *   | organizer       | null      | set      | null              |
 *   | expert_ritual   | set       | set      | null              |
 *   | expert_coaching | set       | null     | set               |
 *
 * Read/render sites switch on `classifyConversation(...).kind` with a `never`
 * exhaustiveness check, so a fourth flavor fails to compile everywhere. This
 * replaces ad-hoc `bookingId != null` / `coachingSessionId != null` ladders that
 * drifted (PR #320 mislabeled ritual-booking expert chats as "Coaching
 * Session").
 *
 * The server may omit a FK (sending `undefined` rather than `null`); treat
 * `undefined` as `null`, matching the existing `!= null` checks.
 */

export type ConversationKind =
  | { kind: "organizer"; ritualId: string }
  | { kind: "expert_ritual"; ritualId: string; bookingId: string }
  | { kind: "expert_coaching"; coachingSessionId: string; bookingId: string }

export interface ConversationKindInput {
  ritualId?: string | null
  bookingId?: string | null
  coachingSessionId?: string | null
}

export function classifyConversation(conv: ConversationKindInput): ConversationKind {
  const ritualId = conv.ritualId ?? null
  const bookingId = conv.bookingId ?? null
  const coachingSessionId = conv.coachingSessionId ?? null

  if (bookingId === null) {
    return { kind: "organizer", ritualId: ritualId as string }
  }
  if (ritualId !== null) {
    return { kind: "expert_ritual", ritualId, bookingId }
  }
  return { kind: "expert_coaching", coachingSessionId: coachingSessionId as string, bookingId }
}

/** True for the two booking-backed expert flavors (not the organizer chat). */
export function isExpertConversation(conv: ConversationKindInput): boolean {
  return classifyConversation(conv).kind !== "organizer"
}
